import React, { useState } from "react";
import { OnboardingData } from "types";
import InputField from "components/fields/InputField";
import { useTranslation } from "react-i18next";

interface OnboardingFormProps {
  onSubmit: (data: OnboardingData) => void;
  isLoading: boolean;
}

const OnboardingForm: React.FC<OnboardingFormProps> = ({ onSubmit, isLoading }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<OnboardingData>({
    orgName: "",
    workspaceName: "",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    subscriptionKey: "",
  });

  const [errors, setErrors] = useState<Partial<OnboardingData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<OnboardingData> = {};

    if (!formData.orgName.trim()) {
      newErrors.orgName = t("onboarding.organizationNameRequired");
    }

    if (!formData.workspaceName.trim()) {
      newErrors.workspaceName = t("onboarding.workspaceNameRequired");
    }

    if (!formData.adminName.trim()) {
      newErrors.adminName = t("onboarding.adminNameRequired");
    }

    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = t("onboarding.adminEmailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
      newErrors.adminEmail = t("onboarding.validEmail");
    }

    if (!formData.adminPassword.trim()) {
      newErrors.adminPassword = t("onboarding.adminPasswordRequired");
    } else if (formData.adminPassword.length < 8) {
      newErrors.adminPassword = t("onboarding.passwordMinLength");
    }

    if (!formData.subscriptionKey.trim()) {
      newErrors.subscriptionKey = t("onboarding.subscriptionKeyRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof OnboardingData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Organization Name */}
      <div>
        <InputField
          variant="auth"
          extra="mb-2"
          label={t("onboarding.organizationName")}
          placeholder={t("onboarding.enterOrganizationName")}
          id="orgName"
          type="text"
          value={formData.orgName}
          onChange={handleInputChange("orgName")}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        {errors.orgName && (
          <p className="text-sm font-medium text-red-500">{errors.orgName}</p>
        )}
      </div>

      {/* Workspace Name */}
      <div>
        <InputField
          variant="auth"
          extra="mb-2"
          label={t("onboarding.workspaceName")}
          placeholder={t("onboarding.enterWorkspaceName")}
          id="workspaceName"
          type="text"
          value={formData.workspaceName}
          onChange={handleInputChange("workspaceName")}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        {errors.workspaceName && (
          <p className="text-sm font-medium text-red-500">{errors.workspaceName}</p>
        )}
      </div>

      {/* Admin Name */}
      <div>
        <InputField
          variant="auth"
          extra="mb-2"
          label={t("onboarding.adminName")}
          placeholder={t("onboarding.enterAdminFullName")}
          id="adminName"
          type="text"
          value={formData.adminName}
          onChange={handleInputChange("adminName")}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        {errors.adminName && (
          <p className="text-sm font-medium text-red-500">{errors.adminName}</p>
        )}
      </div>

      {/* Admin Email */}
      <div>
        <InputField
          variant="auth"
          extra="mb-2"
          label={t("onboarding.adminEmail")}
          placeholder={t("onboarding.adminEmailPlaceholder")}
          id="adminEmail"
          type="email"
          value={formData.adminEmail}
          onChange={handleInputChange("adminEmail")}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        {errors.adminEmail && (
          <p className="text-sm font-medium text-red-500">{errors.adminEmail}</p>
        )}
      </div>

      {/* Admin Password */}
      <div>
        <InputField
          variant="auth"
          extra="mb-2"
          label={t("onboarding.adminPassword")}
          placeholder={t("auth.passwordPlaceholder")}
          id="adminPassword"
          type="password"
          value={formData.adminPassword}
          onChange={handleInputChange("adminPassword")}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        {errors.adminPassword && (
          <p className="text-sm font-medium text-red-500">{errors.adminPassword}</p>
        )}
      </div>

      {/* Subscription Key */}
      <div>
        <InputField
          variant="auth"
          extra="mb-2"
          label={t("onboarding.subscriptionKey")}
          placeholder={t("onboarding.enterSubscriptionKey")}
          id="subscriptionKey"
          type="text"
          value={formData.subscriptionKey}
          onChange={handleInputChange("subscriptionKey")}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        {errors.subscriptionKey && (
          <p className="text-sm font-medium text-red-500">{errors.subscriptionKey}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="linear w-full rounded-xl bg-brand-500 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
      >
        {isLoading ? t("common.activating") : t("common.activate")}
      </button>
    </form>
  );
};

export default OnboardingForm;
